import dayjs from "dayjs";
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react-native";
import React, { useCallback, useState } from "react";
import DateTimePicker, {
  useDefaultClassNames,
} from "react-native-ui-datepicker";

import { Box } from "../../ui/box";
import { Button, ButtonIcon, ButtonText } from "../../ui/button";
import { HStack } from "../../ui/hstack";
import { Icon } from "../../ui/icon";
import { Popover, PopoverBackdrop, PopoverContent } from "../../ui/popover";
import { Text } from "../../ui/text";
import { VStack } from "../../ui/vstack";

export type DateRangeValue = { start: Date | null; end: Date | null };

export type DateRangeQuickPreset = {
  key: string;
  label: string;
  resolve: () => DateRangeValue;
};

type Props = {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  /** Shown on the trigger while no range is set. */
  placeholder?: string;
  /** Optional one-tap ranges rendered above the calendar. */
  presets?: DateRangeQuickPreset[];
};

const EMPTY_RANGE: DateRangeValue = { start: null, end: null };

const formatEdge = (date: Date | null): string =>
  date ? dayjs(date).format("D MMM YYYY") : "…";

/**
 * Standalone date-range picker in a popover — the react-hook-form-bound
 * `forms/dateRange` needs a `control`, so filter UIs that keep their range in
 * plain component state use this instead. Lives here rather than in an app so
 * it owns the `react-native-ui-datepicker` dependency.
 */
const DateRangePopover = React.memo(
  ({ value, onChange, placeholder = "Todas las fechas", presets }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const defaultClassNames = useDefaultClassNames();
    const hasRange = Boolean(value.start || value.end);

    const label = hasRange
      ? `${formatEdge(value.start)} – ${formatEdge(value.end)}`
      : placeholder;

    const handleClear = useCallback(() => {
      onChange(EMPTY_RANGE);
      setIsOpen(false);
    }, [onChange]);

    return (
      <Popover
        placement="bottom left"
        offset={5}
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        trigger={(triggerProps) => (
          <Button
            {...triggerProps}
            action={hasRange ? "brand" : "secondary"}
            variant="outline"
            size="sm"
            rounded="lg"
            className="w-full justify-between"
          >
            <HStack space="xs" className="items-center flex-1 min-w-0">
              <Icon
                as={Calendar}
                size="xs"
                className={hasRange ? "text-brand-600" : "text-typography-500"}
              />
              <ButtonText
                size="xs"
                numberOfLines={1}
                className={hasRange ? "text-brand-700" : "text-typography-600"}
              >
                {label}
              </ButtonText>
            </HStack>
            <ButtonIcon as={ChevronDown} size="xs" />
          </Button>
        )}
      >
        <PopoverBackdrop />
        <PopoverContent size="md" className="p-3 rounded-2xl">
          <VStack space="sm" className="w-[290px]">
            {presets && presets.length > 0 ? (
              <HStack space="xs" className="flex-wrap">
                {presets.map((preset) => (
                  <Button
                    key={preset.key}
                    action="brand"
                    variant="outline"
                    size="xs"
                    rounded="lg"
                    onPress={() => {
                      onChange(preset.resolve());
                      setIsOpen(false);
                    }}
                  >
                    <ButtonText size="2xs">{preset.label}</ButtonText>
                  </Button>
                ))}
              </HStack>
            ) : null}

            <Box>
              <DateTimePicker
                mode="range"
                onChange={(dates) =>
                  onChange({
                    start: dates.startDate
                      ? dayjs(dates.startDate).toDate()
                      : null,
                    end: dates.endDate ? dayjs(dates.endDate).toDate() : null,
                  })
                }
                firstDayOfWeek={1}
                showOutsideDays
                startDate={value.start ?? undefined}
                endDate={value.end ?? undefined}
                navigationPosition="right"
                disableMonthPicker
                disableYearPicker
                containerHeight={230}
                classNames={{
                  ...defaultClassNames,
                  today: "",
                  day_cell: "p-0",
                  day: "group hover:bg-brand-100 rounded-lg",
                  // Light throughout: the range fill is the faintest tint and
                  // the two endpoints only a step above it, so the calendar
                  // reads as a soft band instead of a slab of saturated purple.
                  selected: "bg-brand-300 hover:bg-brand-300",
                  disabled: "opacity-50",
                  range_fill: "bg-brand-50",
                  day_label: "font-body text-xs",
                  month_label: "font-body text-xs capitalize",
                  year_label: "font-body text-xs capitalize",
                  month_selector_label:
                    "font-body text-sm capitalize font-bold",
                  year_selector_label: "font-body text-sm font-bold",
                  weekday_label: "font-body text-xs capitalize",
                  range_end_label: "font-body text-xs font-bold text-brand-900",
                  range_middle_label: "font-body text-xs text-brand-800",
                  range_start_label:
                    "font-body text-xs font-bold text-brand-900",
                  selected_label: "font-body text-xs font-bold text-brand-900",
                  disabled_label: "font-body text-xs",
                  outside_label: "font-body text-xs text-typography-200",
                  today_label: "font-body text-xs",
                  button_prev: "hover:bg-brand-50",
                  button_next: "hover:bg-brand-50",
                }}
                locale="es"
                components={{
                  IconPrev: <Icon as={ChevronLeft} size="sm" />,
                  IconNext: <Icon as={ChevronRight} size="sm" />,
                }}
              />
            </Box>

            <HStack className="items-center justify-between">
              <Text size="2xs" className="text-typography-400 flex-1">
                {hasRange
                  ? "Se muestran las campañas que cruzan el rango."
                  : "Elige una fecha de inicio y una de término."}
              </Text>
              <Button
                variant="link"
                action="muted"
                size="xs"
                isDisabled={!hasRange}
                onPress={handleClear}
              >
                <ButtonText>Limpiar</ButtonText>
              </Button>
            </HStack>
          </VStack>
        </PopoverContent>
      </Popover>
    );
  },
);

DateRangePopover.displayName = "DateRangePopover";

export default DateRangePopover;
